import { Component, HostBinding, Input, OnInit, inject, input } from '@angular/core';
import { FormControl, NgControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { Store } from '@ngxs/store';
import { MatIcon } from '@angular/material/icon';
import { IconNames } from '@app/shared/enums/icon-names';
import { getObjectPropertyFromPath } from '@app/core/utils';

/**
 * The checkbox is meant to be used for a form control that allows users to choose between true or false for a single form control.
 *
 * Usage example:
 * @example
 * <app-ice-checkbox forId="updateImeiLater" label="Jeg har ikke tilgang til telefonen akkurat nå" ngDefaultControl [formControl]="updateImeiLaterControl" />
 **/
@Component({
  selector: 'app-ice-checkbox',
  templateUrl: './ice-checkbox.component.html',
  styleUrl: './ice-checkbox.component.scss',
  imports: [FormsModule, ReactiveFormsModule, NgClass, MatIcon],
})
export class IceCheckboxComponent implements OnInit {
  private controlDirective = inject(NgControl);
  private store = inject(Store);

  IconNames = IconNames;

  // Label
  @Input({ required: true }) label: string;
  @Input({ required: true }) forId;

  // Input
  @Input({ required: false }) readonly = false;
  @Input({ required: false }) name = '';
  @Input() ngxsFormPath: string;

  // Errorstate
  @Input() errorOnTouchedOnly: boolean = false;
  @Input() errorText: string;

  // Testing
  testId = input.required<string>();

  control: FormControl;
  private hasFormBeenVisited = false;

  @HostBinding('class.disabled') get getDisabled() {
    return this.disabled;
  }

  get checked(): boolean {
    return this.control.value;
  }

  get disabled(): boolean {
    return this.control.disabled;
  }

  get showIsRequiredError(): boolean {
    return this.control.hasError('required') && this.isFormVisitedOrControlTouched();
  }

  get isErrorState(): boolean {
    return (
      (this.control.invalid && (this.control.touched || this.isFormVisitedOrControlTouched())) || this.showIsRequiredError
    );
  }

  ngOnInit(): void {
    this.control = this.controlDirective.control as FormControl;

    if (!!this.ngxsFormPath) {
      const state = this.store.snapshot();
      const contextForm = getObjectPropertyFromPath(state, this.ngxsFormPath);
      this.hasFormBeenVisited = contextForm?.dirty;
    }
  }

  private isFormVisitedOrControlTouched(): boolean {
    return this.errorOnTouchedOnly ? this.control.touched : this.hasFormBeenVisited;
  }

  toggleCheckbox($event): void {
    if (this.disabled) {
      return;
    }
    $event.preventDefault();
    this.control.setValue(!this.control.value);
  }
}
